import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import styled from 'styled-components';

import { useModalContext } from '@/components/Modal/Modal';
import useColor from '@/hooks/domain/useColor';
import useKeymap from '@/hooks/pure/useKeymap';
import useAttachmentStore from '@/stores/AttachmentStore';

import ImageViewer from './ImageViewer';
import VideoViewer from './VideoViewer';

const AttachmentModal = () => {
  const color = useColor();

  const {
    attachmentList,
    selectedAttachment,
    setAttachmentList,
    setSelectedAttachment,
  } = useAttachmentStore((state) => ({
    attachmentList: state.attachmentList,
    selectedAttachment: state.selectedAttachment,
    setAttachmentList: state.setAttachmentList,
    setSelectedAttachment: state.setSelectedAttachment,
  }));

  const { closeModal } = useModalContext();
  const [duration, setDuration] = useState<number | null>(null);
  const [size, setSize] = useState<[number, number] | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const index = attachmentList.findIndex(
    (attachment) => attachment.alias === selectedAttachment?.alias,
  );

  const leftHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (index <= 0) return;
    setSelectedAttachment(attachmentList[index - 1]);
  };

  const rightHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (index == -1 || index >= attachmentList.length - 1) return;
    setSelectedAttachment(attachmentList[index + 1]);
  };

  const convertDurationToString = (duration: number) => {
    const sec = String(Math.floor(duration % 60)).padStart(2, '0');
    const min = String(Math.floor(duration / 60)).padStart(2, '0');
    const hour = String(Math.floor(duration / 3600)).padStart(2, '0');

    return hour + ':' + min + ':' + sec;
  };

  const closeHandler = () => {
    closeModal();
    setAttachmentList([]);
    setSelectedAttachment(null);
  };

  useKeymap('Escape', closeHandler);

  return (
    <>
      <Left onClick={leftHandler}>
        <LeftOutlined
          style={{
            fontSize: '24px',
            color: index > 0 ? color.gray100 : color.gray600,
          }}
        />
      </Left>
      <Right onClick={rightHandler}>
        <RightOutlined
          style={{
            fontSize: '24px',
            color:
              index !== -1 && index < attachmentList.length - 1
                ? color.gray100
                : color.gray600,
          }}
        />
      </Right>
      {selectedAttachment &&
        (selectedAttachment?.category === 1 ? (
          <ImageViewer
            alias={selectedAttachment.alias}
            setSize={setSize}
            setFileSize={setFileSize}
          />
        ) : (
          <VideoViewer
            alias={selectedAttachment.alias}
            setSize={setSize}
            setDuration={setDuration}
            setFileSize={setFileSize}
          />
        ))}

      <Info>
        <div className="title" style={{ color: color.gray900 }}>
          {selectedAttachment?.name}
        </div>
        <div className="meta-data" style={{ color: color.gray900 }}>
          {fileSize && `${fileSize / 1000}KB`}
        </div>
        <div className="meta-data" style={{ color: color.gray900 }}>
          {size && `${Math.floor(size[0])} x ${Math.floor(size[1])}`}
        </div>
        <div className="meta-data" style={{ color: color.gray900 }}>
          {selectedAttachment?.category !== 1 && duration
            ? convertDurationToString(duration)
            : ''}
        </div>
      </Info>
    </>
  );
};

export default AttachmentModal;

const Left = styled.div`
  position: fixed;
  top: 50%;
  left: 24px;
  cursor: pointer;
`;

const Right = styled.div`
  position: fixed;
  top: 50%;
  right: 24px;
  cursor: pointer;
`;

const Info = styled.div`
  position: fixed;
  left: 24px;
  top: 24px;

  font-style: normal;
  line-height: 20px;

  .title {
    width: 172px;
    font-weight: 600;
    font-size: 14px;
    color: ${(props) => props.theme.colors.gray100};

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
  }

  .meta-data {
    font-weight: 400;
    font-size: 12px;
    color: ${(props) => props.theme.colors.gray300};
  }
`;
